import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { BaseService } from '@services/base-service.service';
import { UtilitiesService } from '@services/utilities.service';
import { STRING_CONSTANTS } from '@config/constants/string.constants';
import { IGenericNameUrl } from '@shared/interface/generic';
import { PdfService } from '@services/pdf.service';

@Component({
  selector: 'app-document-upload',
  templateUrl: './document-upload.component.html',
  styleUrls: ['./document-upload.component.scss'],
})
export class DocumentUploadComponent implements OnInit, OnChanges {
  @Input() label = 'Dokumen Persetujuan';
  @Input() type?: string | 'FILE' | 'IMAGE';
  @Input() isDisabled = false;

  @Input() required = false;
  @Input() showLabel = true;
  @Input() idx = 0;
  // Item lanjutan tanpa label sendiri (grup multi-dokumen, label cuma di item pertama) — dipisah
  // dari `idx` karena consumer lain reuse `idx` buat index yang gak berkaitan (section/retailer terpisah).
  @Input() isStackedItem = false;

  @Input() maxUploadSize = 5;
  @Input() enableDelete = true;
  @Input() data!: IGenericNameUrl | undefined;
  @Input() customColClass = { label: 'col-lg-4', content: 'col-lg-8' };

  @Output() documentOutput = new EventEmitter<IGenericNameUrl>();
  @Output() loadingState = new EventEmitter();

  ALLOWED_EXTENSIONS: string[] = [''];
  EXT_IMAGE = ['image/jpg', 'image/jpeg', 'image/png'];
  EXT_FILE = ['application/pdf'];

  STRING_CONSTANTS = STRING_CONSTANTS;

  fileUrlSubject = new BehaviorSubject<string>('');
  isLoadingSubject = new BehaviorSubject(false);
  isLoading$ = this.isLoadingSubject.asObservable();

  ctaLabelFile = 'Pilih File';
  hasError = false;
  errorMessage = '';

  style = 'Theme1';

  formData: FormData;
  fileData: { file: File; sizeMb: string } | undefined;
  dataSubject = new BehaviorSubject(this.data);

  constructor(
    private baseService: BaseService,
    private utils: UtilitiesService,
    private pdfService: PdfService
  ) {}

  ngOnInit(): void {
    this.initAllowedExtension();
  }

  // Prefill (mis. mode edit) — sebelumnya di ngAfterViewInit, TAPI itu cuma jalan sekali pas
  // instance ini pertama kali dibuat. *ngFor multi-file di modal Upload Nota pakai
  // `trackBy: trackByIndex`, jadi begitu array notaFiles berubah antar 2 sesi open() yang beda
  // (mis. buka modal create dulu dengan 1 slot, lalu buka modal edit dengan 2 slot), Angular
  // nge-REUSE instance index 0 yang lama alih-alih destroy+recreate (key index sama) — instance
  // itu udah lewat ngOnInit/ngAfterViewInit sejak jauh sebelumnya dengan data lama, jadi [data]
  // baru yang masuk gak pernah kebaca. ngOnChanges dipanggil setiap kali input berubah (termasuk
  // saat instance di-reuse dengan value baru), jadi ini satu-satunya hook yang tepat buat ini.
  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.data) return;
    this.dataSubject.next(this.data);
    if (this.hasInputData()) {
      // Bug ditemukan 2026-08-28: `type` cuma pernah di-set dari upload FRESH (handleFileChange,
      // baca File.type asli) — prefill (mode edit) gak pernah nyentuh `type` sama sekali, jadi tetap
      // undefined dan preview SELALU jatuh ke previewImage (template: `type === 'FILE' ? previewFile
      // : previewImage`), termasuk buat dokumen PDF. Infer dari URL (pola sama app-document-preview).
      this.type = this.utils.getTypeDoc(this.data?.url as string);
      this.setFileData(this.data?.url as string);
    } else {
      // MAI-2171 (QA Failed) — instance ini bisa di-reuse Angular lintas 2 sesi `open()` modal
      // yang beda (lihat komentar ngOnChanges di atas soal trackBy index-based). Kalau `data`
      // balik ke kosong (bukan prefill baru, tapi form di-reset — mis. modal Upload Nota dibuka
      // ulang), preview/fileData dari sesi SEBELUMNYA harus ikut ke-reset juga, bukan cuma
      // dataSubject — sebelumnya cabang ini gak ada sama sekali, jadi file lama tetap kelihatan
      // walau parent udah nganggep slot itu kosong (Submit correctly disabled tapi UI membingungkan).
      this.resetDisplayState();
    }
  }

  private resetDisplayState(): void {
    this.fileUrlSubject.next('');
    this.hasError = false;
    this.fileData = undefined;
    this.type = undefined;
    this.initAllowedExtension();
    this.ctaLabelFile = 'Pilih File';
  }

  get UniqueLabelID() {
    const _label = this.label.split(' ').join('');
    return _label + '__' + this.idx;
  }

  onFileChange(e: any) {
    if (!e) return;
    this.hasError = false;
    return this.handleFileChange(e.target.files);
  }

  handleFileChange(e: File[]) {
    const file = e[0];

    if (!file) return;
    if (!this.isAllowedExtension(file) || !file.type) return this.setError('INVALID_FILE_EXTENSION');

    // check file type and set type state
    this.type = this.EXT_IMAGE.includes(file.type) ? 'IMAGE' : this.EXT_FILE.includes(file.type) ? 'FILE' : undefined;

    this.setLoading(true);
    const fileSizeMb = this.fileSizeToMb(file.size);
    if (fileSizeMb > this.maxUploadSize) return this.setError('MAX_FILE_SIZE');

    this.formData = new FormData();
    this.formData.append('file', file, file.name);
    this.formData.append('category', this.type as string);

    this.fileData = { file, sizeMb: `${fileSizeMb} Mb` };
    this.dataReader(file).then(() => this.handleUpload(this.formData));
  }

  async dataReader(e: File) {
    if (!e) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const target = e.target && e.target.result;
      this.fileUrlSubject.next(target as string);
    };

    reader.readAsDataURL(e);
  }

  handleUpload(formData: FormData) {
    // filter(!!res) - BaseService selalu emit null-seed duluan (BehaviorSubject), BUKAN kegagalan
    // asli. Tanpa ini, setError() ke-trigger sinkron di awal (matiin loading sebelum network kelar).
    this.baseService
      .uploadData<any>(formData, { disableModalPopupError: true })
      .pipe(filter((res) => !!res))
      .subscribe((res) => {
        this.ctaLabelFile = '';

        if (res.success) {
          const fileName = this.fileData?.file.name as string;
          this.documentOutput.emit({ name: fileName, url: res.data.url });
          this.ctaLabelFile = this.getReplaceCtaLabel();
          this.setFileData(res.data.url);
        }

        if (res.errors) {
          this.handleSwitchErrors(res.errors);
        }

        this.setLoading(false);
      });
  }

  handleSwitchErrors(res: [{ code: number; title: string; message: string }]) {
    switch (res[0].code) {
      case 0:
        this.setError('ERR_INTERNET_DISCONNECTED');
        break;
      default:
        this.setError('ERR_FAILED');
        break;
    }
  }

  handleDownload() {
    this.isLoadingSubject.next(true);

    const fileUrl = this.fileUrlSubject.value ? this.fileUrlSubject.value : URL.createObjectURL(this.fileData?.file as File);
    const fileName = this.fileData?.file.name as string;

    const _download$ = this.pdfService.downloadPdf({ name: fileName, url: fileUrl });
    _download$.subscribe((resp) => {
      if (!resp) return;
      setTimeout(() => this.isLoadingSubject.next(false), 250);
    });
  }

  handleRemoveFile() {
    this.setLoading(true);
    setTimeout(() => {
      this.fileUrlSubject.next('');
      this.documentOutput.emit();

      this.hasError = false;
      this.fileData = undefined;
      this.type = undefined;

      this.dataSubject.next(undefined);
      this.initAllowedExtension();
      this.setLoading(false);
    }, 250);
  }

  setLoading(state: boolean) {
    this.isLoadingSubject.next(state);
    this.loadingState.emit(state);
  }

  setFileData(url: string) {
    this.ctaLabelFile = this.getReplaceCtaLabel();
    return this.fileUrlSubject.next(url);
  }

  getReplaceCtaLabel() {
    return this.type === 'IMAGE' ? 'Ganti Foto' : 'Ganti File';
  }

  setError(type: string) {
    if (!type) return;

    this.hasError = true;
    const errMsg = 'File yang diupload ';
    const errUploadFailed = 'File gagal diupload';

    switch (type) {
      case 'MAX_FILE_SIZE':
        this.errorMessage = `${errMsg} lebih dari ${this.maxUploadSize}Mb`;
        break;

      case 'INVALID_FILE_EXTENSION':
        this.errorMessage = `Format ${errMsg} tidak didukung.`;

        // get extension name only
        const _allowedExtensions = this.ALLOWED_EXTENSIONS.map((item) => {
          return item.split('/')[1];
        });

        this.errorMessage += `<br>Format file yang didukung: ${this.utils.arrayStringJoin(_allowedExtensions, null, ',', true)}`;
        break;

      case 'ERR_INTERNET_DISCONNECTED':
        this.errorMessage = `${errUploadFailed} karena koneksi internet.`;
        break;

      case 'ERR_FAILED':
        this.errorMessage = `${errUploadFailed} karena kesalahan sistem.`;
        break;
    }

    // WAJIB di semua case, bukan cuma sebagian — dulu cuma di 2 dari 4 case, bikin CTA hilang
    // permanen (blank) kalau upload gagal network/system, padahal preview lokal tetap kelihatan.
    this.ctaLabelFile = this.getReplaceCtaLabel();
    this.setLoading(false);
  }

  initAllowedExtension() {
    if (!this.type) return (this.ALLOWED_EXTENSIONS = [...this.EXT_IMAGE, ...this.EXT_FILE]);
    return (this.ALLOWED_EXTENSIONS = this.type === 'IMAGE' ? ['image/jpg', 'image/jpeg', 'image/png'] : this.type === 'FILE' ? ['application/pdf'] : []);
  }

  isAllowedExtension(file: File) {
    return this.ALLOWED_EXTENSIONS.includes(file.type);
  }

  fileSizeToMb(size: number) {
    return +(size / 1024 ** 2).toFixed(2);
  }

  hasFileData() {
    return !!(this.fileData && !this.utils.isObjectEmpty(this.fileData));
  }

  hasInputData() {
    return !this.utils.isObjectEmpty(this.dataSubject.value as IGenericNameUrl);
  }
}
